import { HelpCircle } from 'lucide-react';
import { Link } from '@mui/material';

import { helpUrl, type HelpSlug } from '../lib/helpUrl';

/**
 * Props for {@link HelpLink}.
 */
export interface HelpLinkProps {
    /**
     * Help-article slug to deep-link to. Must appear in the canonical
     * `HELP_ARTICLES` manifest — the type union enforces this at compile time,
     * so a typo or stale slug fails the build rather than producing a dead link.
     */
    slug: HelpSlug;
    /**
     * Visible label rendered after the icon. Also forms the accessible name
     * (prefixed with "Help:") so screen readers convey both the action and the
     * topic. Defaults to "Learn more".
     */
    label?: string;
}

/**
 * Inline contextual help link.
 *
 * Pair with any high-friction form, modal, or settings panel to offer a
 * one-click jump into the relevant help article. Renders as a subtle inline
 * anchor (icon + text, not a button) so it sits naturally next to surrounding
 * form copy. Opens in a new tab with `rel="noopener noreferrer"` so the host
 * app session is never replaced.
 *
 * All URLs flow through {@link helpUrl} so the slug union is enforced at the
 * type level — there is no way to ship a link that points at a non-existent
 * article.
 */
export default function HelpLink({ slug, label = 'Learn more' }: HelpLinkProps): JSX.Element {
    return (
        <Link
            href={helpUrl(slug)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Help: ${label}`}
            underline="hover"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.875rem',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
            }}
        >
            <HelpCircle size={14} aria-hidden="true" />
            <span>{label}</span>
        </Link>
    );
}
